import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@/queries/useUser';
import { useStanRouter } from '@/hooks/useStanRouter';
import { OnboardingStep } from './OnboardingStep';
import { SocialLinksModal } from './SocialLinksModal';
import { ProductSelectionView } from '../DynamicDashboardV8/ProductSelectionView';
import { PRODUCT_TYPE_TO_PAGE_TYPE } from '@/features/dashboard/constants/productTypeMap';
import { StanCrop } from '@/components';
import { StanFileUpload, StanFileUploadRef } from '@/components/core-components/StanFileUpload/StanFileUpload';
import { uploadFile } from '@/lib/api/fileUpload';
import { useUpdateProfileImage } from '@/features/profile/queries';
import { stanNotify } from '@/lib/stanNotify';
import { isAxiosError } from 'axios';
import { stanAnalytics } from '@/lib/stanAnalytics';
import { useOnboardingStepStatus } from '@/hooks/useOnboardingStepStatus';
import { useOnboardingCurrentStep } from '@/hooks/useOnboardingCurrentStep';
import { useOnboardingSessionStorage } from '@/hooks/useOnboardingSessionStorage';
import { useAskStanley } from '@/features/ask-stanley/hooks';

import IcUser from '@/assets/icons/ic-user-01.svg';
import ImgDirectDeposit from '@/assets/images/dashboard/onboarding-direct-deposit.svg';
import ImgSocials from '@/assets/images/dashboard/onboarding-socials.svg';
import ImgProduct from '@/assets/images/dashboard/onboarding-product.svg';

export function OnboardingChecklist() {
    const { user, refetchUser } = useUser();
    const navigate = useNavigate();
    const { push } = useStanRouter();
    const fileUploadRef = useRef<StanFileUploadRef>(null);
    const [pickedFile, setPickedFile] = useState<File | null>(null);
    const [isCropOpen, setIsCropOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isSocialLinksModalOpen, setIsSocialLinksModalOpen] = useState(false);
    const [showProductSelection, setShowProductSelection] = useState(false);
    const { mutateAsync: updateProfileImageMutation } = useUpdateProfileImage();
    const { openChat } = useAskStanley();

    // Use hooks for onboarding logic
    const { isProfileImageUploaded, isSocialLinksAdded, isFirstProductAdded, isDirectDepositAdded } =
        useOnboardingStepStatus();

    const { getStepReady, markStepReady, getAllStepsReady, refreshKey } = useOnboardingSessionStorage();

    const currentStep = useOnboardingCurrentStep();

    // Use refreshKey to ensure component re-renders when steps are marked ready
    // This ensures currentStep recalculates when refreshKey changes
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    refreshKey;

    // Check if step 3 should be shown (not completed and not skipped)
    const depositReady = getStepReady('depositSetup');
    const showDirectDepositStep = !isDirectDepositAdded && !depositReady;

    // Check if step 4 should be shown (not completed and not skipped)
    const firstProductReady = getStepReady('firstProduct');
    const showFirstProductStep = !isFirstProductAdded && !firstProductReady;

    // Calculate completed tasks (including skipped steps)
    const completedTasks = useMemo(() => {
        const stepsReady = getAllStepsReady();

        let count = 0;
        if (isProfileImageUploaded || stepsReady.profileImage) count++;
        if (isSocialLinksAdded || stepsReady.socialLinks) count++;
        if (isDirectDepositAdded || stepsReady.depositSetup) count++;
        if (isFirstProductAdded || stepsReady.firstProduct) count++;
        return count;
    }, [
        isProfileImageUploaded,
        isSocialLinksAdded,
        isDirectDepositAdded,
        isFirstProductAdded,
        getAllStepsReady,
        refreshKey,
    ]);

    const openFilePicker = useCallback(() => {
        fileUploadRef.current?.onPickFile();
    }, []);

    const onFilePicked = useCallback((files: File[]) => {
        const file = files[0] || null;
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            stanNotify({
                type: 'error',
                title: 'Invalid File Type',
                text: 'Please select an image file.',
                duration: 3000,
            });
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            stanNotify({
                type: 'error',
                title: 'File Too Large',
                text: 'File size must be less than 5MB.',
                duration: 5000,
            });
            return;
        }
        setPickedFile(file);
        setIsCropOpen(true);
    }, []);

    const handleAvatarCropped = useCallback(
        async ({ croppedBlob }: { croppedBlob: Blob; croppedDataUrl: string }) => {
            try {
                setIsUploading(true);

                const file = new File(
                    [croppedBlob],
                    pickedFile?.name.replace(/\.[^.]+$/, '.png') || 'profile-image.png',
                    {
                        type: 'image/png',
                    }
                );

                const { url } = await uploadFile(file, { objectType: 'profile-image' });
                await updateProfileImageMutation(url);
                await refetchUser();

                // Mark step 1 as ready for next step
                markStepReady('profileImage');

                // Track completion event
                stanAnalytics('onboarding-dashboard-profile-image', {
                    props: { action: 'done' },
                });

                // Close modal and reset state only after everything is complete
                setIsCropOpen(false);
                setPickedFile(null);
                setIsUploading(false);

                stanNotify({
                    type: 'success',
                    title: 'Profile Updated',
                    text: 'Your profile picture has been updated successfully!',
                    duration: 3000,
                });
            } catch (error) {
                setIsUploading(false);
                let errorMessage = 'Failed to upload avatar. Please try again.';
                let errorTitle = 'Upload Failed';

                if (error instanceof Error && (error as Error & { isFileSizeError?: boolean }).isFileSizeError) {
                    errorTitle = 'File Size Error';
                    errorMessage = error.message;
                } else if (isAxiosError(error)) {
                    errorMessage = error.response?.data?.message || error.message;
                } else if (error instanceof Error) {
                    errorMessage = error.message;
                }

                stanNotify({
                    type: 'error',
                    title: errorTitle,
                    text: errorMessage,
                    duration: 5000,
                });
            }
        },
        [pickedFile, updateProfileImageMutation, refetchUser, markStepReady]
    );

    const handleProfileImage = () => {
        stanAnalytics('dynamic-dashboard-step-click', {
            props: { step: 'profile-image' },
        });
        openFilePicker();
    };

    const handleSocialLinks = () => {
        stanAnalytics('dynamic-dashboard-step-click', {
            props: { step: 'social-links' },
        });
        setIsSocialLinksModalOpen(true);
    };

    const handleSocialLinksModalClose = () => {
        setIsSocialLinksModalOpen(false);
    };

    const handleSocialLinksSave = async () => {
        // Mark step 2 as ready for next step
        markStepReady('socialLinks');

        // Track completion event
        stanAnalytics('onboarding-dashboard-social-links', {
            props: { action: 'done' },
        });

        // Refetch user to update onboarding status
        await refetchUser();
    };

    const handleDirectDeposit = () => {
        stanAnalytics('dynamic-dashboard-step-click', {
            props: { step: 'payment-info' },
        });
        navigate('/account_settings?tab=store');
    };

    const handleSkipDirectDeposit = () => {
        stanAnalytics('onboarding-dashboard-payment-info', {
            props: { action: 'skip' },
        });
        // Mark step 3 as ready for next step
        markStepReady('depositSetup');
        // Force refresh to update currentStep
        window.location.reload();
    };

    const handleFirstProduct = () => {
        stanAnalytics('dynamic-dashboard-step-click', {
            props: { step: 'first-product' },
        });
        // Use the same logic as "Add a product" action card
        openChat('dashboard_card_product_selection');
        setShowProductSelection(true);
    };

    const handleProductSelect = (productType: string) => {
        const storeId = user?.default_store_id;
        if (!storeId) {
            return;
        }

        const pageType = PRODUCT_TYPE_TO_PAGE_TYPE[productType];
        if (!pageType) {
            return;
        }

        push(`/stores/${storeId}/page/${pageType}/create`);
    };

    const handleBackToDashboard = () => {
        setShowProductSelection(false);
    };

    // Show product selection view if active
    if (showProductSelection) {
        return <ProductSelectionView onBack={handleBackToDashboard} onProductSelect={handleProductSelect} />;
    }

    return (
        <div className='flex w-full flex-col gap-6'>
            <h1> Hey {user?.full_name.split(' ')[0] || user?.username}, follow these steps to crush your goal!</h1>
            <div className='mb-2 hidden md:block'>
                <h2 className='heading-1 mb-2'>Store Launch Checklist</h2>
                <p className='text-gray text-sm'>{completedTasks}/4 tasks completed</p>
            </div>

            <div className='flex flex-col gap-2'>
                {/* Step 1: Profile Image - only show if not completed */}
                {!isProfileImageUploaded && (
                    <OnboardingStep
                        stepNumber={1}
                        title='Add Your Profile Picture'
                        description='Personalize your Stan Store by adding your favorite headshot.'
                        buttonText='Add Profile Picture'
                        isActive={currentStep === 1}
                        isCompleted={isProfileImageUploaded}
                        onClick={handleProfileImage}
                        placeholderIcon={IcUser}
                        profileImageUrl={user?.profile_image}
                        userName={user?.full_name || user?.username}
                    />
                )}

                {/* Step 2: Social Links - only show if not completed */}
                {!isSocialLinksAdded && (
                    <OnboardingStep
                        stepNumber={2}
                        title='Add Your Socials'
                        description='Connect all of your different social media accounts'
                        buttonText='Add Social Links'
                        isActive={currentStep === 2}
                        isCompleted={isSocialLinksAdded}
                        onClick={handleSocialLinks}
                        image={ImgSocials}
                    />
                )}

                {/* Step 3: Direct Deposit - only show if not completed and not skipped */}
                {showDirectDepositStep && (
                    <OnboardingStep
                        stepNumber={3}
                        title='Set up Your Direct Deposit'
                        description="You're here to make money, right? Let us know where to send your earnings."
                        subtitle='Enjoy 0% transaction fees on Stan.'
                        buttonText='Set up Direct Deposit'
                        isActive={currentStep === 3}
                        isCompleted={isDirectDepositAdded}
                        onClick={handleDirectDeposit}
                        skippable
                        onSkip={handleSkipDirectDeposit}
                        image={ImgDirectDeposit}
                    />
                )}

                {/* Step 4: First Product - only show if not completed and not skipped */}
                {showFirstProductStep && (
                    <OnboardingStep
                        stepNumber={4}
                        title='Create Your First Product'
                        description='Sell any Digital Product, Service, or Calendar Booking all-in-one place.'
                        buttonText='Create Product'
                        isActive={currentStep === 4}
                        isCompleted={isFirstProductAdded}
                        onClick={handleFirstProduct}
                        image={ImgProduct}
                    />
                )}
            </div>

            {/* File upload component */}
            <StanFileUpload
                ref={fileUploadRef}
                accept='image/png,image/jpeg,image/jpg,image/webp'
                confirmUpload={false}
                onFilePicked={onFilePicked}
            />

            {/* Crop modal */}
            <StanCrop
                isOpen={isCropOpen}
                imageFile={pickedFile}
                aspectRatio={1}
                width={600}
                height={600}
                borderRadius='circle'
                isLoading={isUploading}
                onClose={() => {
                    // Prevent closing during upload
                    if (!isUploading) {
                        setIsCropOpen(false);
                        setPickedFile(null);
                    }
                }}
                onConfirm={({ croppedBlob, croppedDataUrl }) => handleAvatarCropped({ croppedBlob, croppedDataUrl })}
            />

            {/* Social Links Modal */}
            <SocialLinksModal
                isOpen={isSocialLinksModalOpen}
                onClose={handleSocialLinksModalClose}
                onSave={handleSocialLinksSave}
            />
        </div>
    );
}
